import { FormFields, useForm } from '@sequencehq/utils'
import { greaterThan, required } from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import { PricingEditorReducerState } from 'common/drawers/PricingEditor/domain'
import { defaultAvailableFrequenciesOptions } from 'common/drawers/PricingEditor/domain/pricingEditor.constants'
import { useMemo } from 'react'
import { useCommonFieldConfig } from '../common/useCommonFieldConfig'
import { useSyncFormValidity } from '../common/useSyncFormValidity'
import { Currency } from '@sequencehq/api'
import { commonPriceDetails } from '../common/ReadOnlyDetails/readOnlyDetails.utils'
import { useUsageMetricParameterFieldConfig } from '../common/UsageMetrics/useUsageMetricParameterFieldConfig'
import { useUsageMetrics } from '../../../communication/external/useUsageMetrics'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { usageMetricIdValidator } from 'common/drawers/PricingEditor/utils/validators/usageMetricId.validators'
import { pick } from 'lodash/fp'
import { dequal } from 'dequal'

type CommonFields =
  PricingEditorReducerState['data']['pricingEditorData']['common']
type PackagedPriceFields =
  PricingEditorReducerState['data']['pricingEditorData']['PACKAGE']

type PackagedPriceFormFieldConfig = Omit<
  FormFields<CommonFields>,
  'pricingModel' | 'currency'
> & {
  currency: FormFields<CommonFields>['currency'] & {
    hidden: boolean
  }
} & FormFields<PackagedPriceFields>

type UsePackagedPriceForm = () => {
  fieldsConfig: PackagedPriceFormFieldConfig
  currency: Currency
  priceDetails: { label: string; value: string }[]
}

export const usePackagedPriceForm: UsePackagedPriceForm = () => {
  const pricingEditorContext = usePricingEditorDomainContext()
  const flags = useFeatureFlags()

  const commonFields = useCommonFieldConfig<
    PackagedPriceFields & CommonFields
  >()
  const parameterFields = useUsageMetricParameterFieldConfig<
    PackagedPriceFields & CommonFields
  >(
    pricingEditorContext.queries.rawData.data.pricingEditorData.PACKAGE
      .usageMetricId,
    () => false
  )
  /**
   * Allow for the on demand billing frequency if the flag is enabled, or the value has already been set (i.e. the flag is now disabled,
   * but a price was set using it). However, when the flag is disabled this will act as trap door state, and will cause the value to
   * disappear from selection once changed.
   */
  const onDemandFrequencyAvailable =
    pricingEditorContext.queries.rawData.data.pricingEditorData.PACKAGE
      .billingFrequency === 'ON_DEMAND' || flags.useOnDemandBillingFrequency

  const perEventFrequencyAvailable =
    pricingEditorContext.queries.rawData.data.pricingEditorData.PACKAGE
      .billingFrequency === 'PER_EVENT' || flags.usePerEventBilling

  const { usageMetrics } = useUsageMetrics()
  const validateUsageMetricId = usageMetricIdValidator(usageMetrics)

  const { fields, queries, mutators } = useForm({
    value: {
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.common,
      ...pricingEditorContext.queries.rawData.data.pricingEditorData.PACKAGE
    },
    reinitializeOnValueChange: (params: {
      newData: PackagedPriceFields & CommonFields
      prevData: (PackagedPriceFields & CommonFields) | undefined
    }) => {
      const propertiesToCompare = pick([
        'productId',
        'listPriceId',
        'usageMetricId'
      ])
      return !dequal(
        propertiesToCompare(params.newData),
        propertiesToCompare(params.prevData)
      )
    },
    fieldConfiguration: [
      ...commonFields.fields,
      {
        property: 'usageMetricId',
        validation: [required, validateUsageMetricId]
      },
      {
        property: 'billingFrequency',
        options: [
          ...defaultAvailableFrequenciesOptions,
          ...(onDemandFrequencyAvailable
            ? [
                {
                  value: 'ON_DEMAND',
                  label: 'On demand'
                }
              ]
            : []),
          ...(perEventFrequencyAvailable
            ? [
                {
                  value: 'PER_EVENT',
                  label: 'Per event'
                }
              ]
            : [])
        ],
        validation: [required]
      },
      {
        property: 'packageSize',
        validation: [required, greaterThan(0, "Package size can't be zero")]
      },
      {
        property: 'pricePerPackage',
        validation: [
          required,
          greaterThan(0, "Price per package can't be zero")
        ]
      },
      ...parameterFields.fields
    ],
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    disabled:
      !pricingEditorContext.queries.availableFeatures.common.form.available
        .enabled,
    onChange: newData => {
      const { name, currency, ...rest } = newData

      pricingEditorContext.mutators.common.updatePricingEditorData({
        common: {
          name,
          currency
        },
        PACKAGE: rest
      })
    }
  })

  /**
   * A usage metric's parameters come from the metric, so their property names
   * aren't known up front. They all render in the additional fields section.
   */
  const parameterFieldNames = parameterFields.fields.map(
    field => field.property
  )

  useSyncFormValidity({
    isFormValid: queries.isValid,
    formValidationResults: queries.validationResults,
    additionalFields: [
      'billingFrequency',
      'usageMetricId',
      ...parameterFieldNames
    ]
  })

  const enhancedFields = useMemo(() => {
    return {
      ...fields,
      currency: {
        ...fields.currency,
        hidden: Boolean(
          pricingEditorContext.queries.rawData.configuration.currency
        )
      }
    }
  }, [fields, pricingEditorContext.queries.rawData.configuration.currency])

  const priceDetails = useMemo(
    () => [
      ...commonPriceDetails({
        taxCategoryName:
          pricingEditorContext.queries.rawData.data.product.taxCategoryName,
        revenueRecognitionMethod:
          pricingEditorContext.queries.rawData.data.product
            .revenueRecognitionMethod,
        pricingModel: 'PACKAGE',
        billingFrequency: queries.formData.billingFrequency,
        billingType: queries.formData.billingType
      }),
      {
        label: 'Usage metric',
        value:
          usageMetrics.find(
            metric => metric.id === queries.formData.usageMetricId
          )?.label ?? 'Not selected'
      }
    ],
    [
      usageMetrics,
      queries.formData.billingFrequency,
      queries.formData.billingType,
      queries.formData.usageMetricId,
      pricingEditorContext.queries.rawData.data.product.taxCategoryName,
      pricingEditorContext.queries.rawData.data.product.revenueRecognitionMethod
    ]
  )

  return {
    fieldsConfig: enhancedFields,
    currency: queries.formData.currency,
    priceDetails
  }
}
